import { TuiDay, TuiDayRange, TuiValueTransformer } from '@taiga-ui/cdk';
import { Interval } from 'luxon';

import { convertDateTimeToTimeZone, getTimeZone, isoDate, toTuiDay } from './date.fns';
import { fromIsoToTuiDay } from './taiga-ui/taiga-ui-date.fns';

/**
 * Transforms between a TuiDay and a full ISO-8601 date string in UTC, at midnight current timezone.
 */
export class DateTransformer implements TuiValueTransformer<TuiDay | null, string> {
  fromControlValue(controlValue: string): TuiDay | null {
    return controlValue ? toTuiDay(controlValue) : null;
  }

  toControlValue(day: TuiDay | null): string {
    return isoDate(day, null);
  }
}

/**
 * Transforms between a TuiDay and a string in the format YYYY-MM-DD.
 */
export class DateOnlyTransformer implements TuiValueTransformer<TuiDay | null, string | null> {
  fromControlValue(controlValue: string | null): TuiDay | null {
    return controlValue ? TuiDay.jsonParse(controlValue) : null;
  }

  toControlValue(day: TuiDay | null): string | null {
    return day?.toJSON() ?? null;
  }
}

/**
 * Transforms between a TuiDay and a full ISO-8601 date string in UTC,
 * at midnight based on the given target timezone.
 * Target time zone defaults to UTC+1 if not provided.
 */
export class ZonedDateTransformer implements TuiValueTransformer<TuiDay | null, string> {
  readonly targetTimeZone: string = 'UTC+1';

  constructor(targetTimeZone?: string) {
    if (targetTimeZone !== undefined) {
      this.targetTimeZone = targetTimeZone;
    }
  }

  fromControlValue(controlValue: string): TuiDay | null {
    if (controlValue) {
      const convertedDate = convertDateTimeToTimeZone(controlValue, getTimeZone(), this.targetTimeZone);
      return toTuiDay(convertedDate);
    }
    return null;
  }

  toControlValue(day: TuiDay | null): string {
    const isoDateString = isoDate(day, null);
    if (isoDateString !== '') {
      return convertDateTimeToTimeZone(isoDateString, this.targetTimeZone, getTimeZone());
    }
    return isoDateString;
  }
}

/**
 * Transforms between a TuiDayRange and a Luxon Interval.
 * Converts TuiDayRange to a full Interval (from start of first day to end of last day).
 * Converts Interval or serialized Interval back to TuiDayRange.
 */
export class DateRangeTransformer implements TuiValueTransformer<
  TuiDayRange | null,
  string | Interval<true> | TuiDayRange | null
> {
  readonly zone: string;

  constructor(zone: string = 'UTC') {
    this.zone = zone;
  }

  fromControlValue(controlValue: string | Interval<true> | TuiDayRange | null): TuiDayRange | null {
    if (!controlValue) {
      return null;
    }

    if (controlValue instanceof TuiDayRange) {
      return controlValue;
    }

    if (typeof controlValue === 'string') {
      return TuiDayRange.normalizeParse(controlValue);
    }

    if ('start' in controlValue && 'end' in controlValue) {
      const { start, end } = controlValue;
      // start/end are Luxon DateTime; convert to ISO first for the helper
      return new TuiDayRange(fromIsoToTuiDay(start.toISO()), fromIsoToTuiDay(end.toISO()));
    }

    return null;
  }

  toControlValue(value: TuiDayRange | Interval<true> | { s?: string; e?: string } | null): TuiDayRange | string {
    if (!value) {
      return '';
    }

    if (value instanceof TuiDayRange) {
      return value;
    }

    if ('start' in (value as Interval<true>) && 'end' in (value as Interval<true>)) {
      const interval = value as Interval<true>;
      return new TuiDayRange(fromIsoToTuiDay(interval.start.toISO()), fromIsoToTuiDay(interval.end.toISO()));
    }

    if ('s' in (value as { s?: string; e?: string }) && 'e' in (value as { s?: string; e?: string })) {
      const { s, e } = value as { s?: string; e?: string };
      if (s && e) {
        return new TuiDayRange(fromIsoToTuiDay(s), fromIsoToTuiDay(e));
      }
    }

    if (typeof value === 'string') {
      return TuiDayRange.normalizeParse(value);
    }

    return '';
  }
}
